import { useEffect } from 'react';
import { useSelection } from '../../npContext';
import type { UserData } from './types';
import { sendGTMEvent } from './utils';

const sha256Hash = async (text: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const USER_DATA_KEY = 'events_user_data';

export const getUserData = (): UserData | undefined => {
    if (typeof window === 'undefined') {
        console.error('Attempted to call getUserData on server side');
        return undefined;
    }
    const stored = localStorage.getItem(USER_DATA_KEY);
    if (!stored) return undefined;

    try {
        return JSON.parse(stored) as UserData;
    } catch {
        return undefined;
    }
};

export const setUserData = async (userData: UserData) => {
    const oldUserData = getUserData();
    const newUserData = { ...oldUserData, ...userData };

    if (newUserData.email) {
        newUserData.sha256_hashed_email = await sha256Hash(newUserData.email);
    }
    if (newUserData.phone_number) {
        newUserData.sha256_hashed_phone_number = await sha256Hash(newUserData.phone_number);
    }
    if (newUserData.first_name) {
        newUserData.sha256_hashed_first_name = await sha256Hash(newUserData.first_name);
    }
    if (newUserData.last_name) {
        newUserData.sha256_hashed_last_name = await sha256Hash(newUserData.last_name);
    }
    if (newUserData.street) {
        newUserData.sha256_hashed_street = await sha256Hash(newUserData.street);
    }
    if (newUserData.city) {
        newUserData.sha256_hashed_city = await sha256Hash(newUserData.city);
    }
    if (newUserData.country) {
        newUserData.sha256_hashed_country = await sha256Hash(newUserData.country);
    }
    if (newUserData.postal_code) {
        newUserData.sha256_hashed_postal_code = await sha256Hash(newUserData.postal_code);
    }

    localStorage.setItem(USER_DATA_KEY, JSON.stringify(newUserData));

    sendGTMEvent({
        event: 'user_data_updated',
        user_data: newUserData,
    });

    return newUserData;
};

const useSyncGTMUserData = () => {
    const selection = useSelection();

    useEffect(() => {
        const updateUserData = async () => {
            const userData: UserData = {};

            if (selection?.address.email) {
                userData.email = selection.address.email;
            }

            if (selection?.address.phoneNumber) {
                userData.phone_number = selection.address.phoneNumber;
            }

            if (selection?.address.firstName) {
                userData.first_name = selection.address.firstName;
            }

            if (selection?.address.lastName) {
                userData.last_name = selection.address.lastName;
            }

            if (selection?.address.address1) {
                userData.street = selection.address.address1;
            }

            if (selection?.address.city) {
                userData.city = selection.address.city;
            }

            if (selection?.address.country) {
                userData.country = selection.address.country;
            }

            if (selection?.address.zipCode) {
                userData.postal_code = selection.address.zipCode;
            }

            if (Object.keys(userData).length > 0) {
                await setUserData(userData);
            }
        };

        void updateUserData();
    }, [
        selection?.address.email,
        selection?.address.phoneNumber,
        selection?.address.firstName,
        selection?.address.lastName,
        selection?.address.address1,
        selection?.address.city,
        selection?.address.country,
        selection?.address.zipCode,
    ]);
};

export const SyncGTMUserData = () => {
    useSyncGTMUserData();

    return null;
};
